import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Check, Save, Lock, Unlock } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api, adminHeaders, getToken } from "@/lib/api";
import { formatTimeCs, parseTimeCs } from "@/lib/time";
import { SUIT_SYMBOL, SUIT_COLOR, SUIT_LABEL } from "@/lib/suits";
import { BottomTab } from "@/components/BottomTab";

function GameRow({ gender, athlete, gameNumber, onSaved }) {
  const isReps = gameNumber === 2;
  const existing = athlete.games.find((g) => g.game_number === gameNumber) || {};
  const [time, setTime] = useState(
    existing.time_cs != null ? (isReps ? String(existing.time_cs) : formatTimeCs(existing.time_cs)) : ""
  );
  const [override, setOverride] = useState(!!existing.points_override);
  const [points, setPoints] = useState(existing.points ?? 0);
  const [lane, setLane] = useState(
    existing.lane != null && existing.lane !== "" ? String(existing.lane) : (athlete.lane || "")
  );
  const [busy, setBusy] = useState(false);

  const save = async () => {
    let cs = null;
    if (time.trim()) {
      cs = isReps ? parseInt(time, 10) : parseTimeCs(time);
      if (cs === null || Number.isNaN(cs)) { toast.error(isReps ? "Valore non valido" : "Tempo non valido"); return; }
    }
    setBusy(true);
    try {
      await api.post("/games/score", {
        athlete_id: athlete.athlete_id, game_number: gameNumber,
        time_cs: cs, points: Number(points) || 0, points_override: override,
        lane: lane.trim() || null,
      }, { headers: adminHeaders() });
      toast.success(`G${gameNumber} salvato`);
      onSaved();
    } catch { toast.error("Errore"); } finally { setBusy(false); }
  };

  const input = "bg-surface rounded-lg px-2 py-2 font-bold text-onsurface outline-none focus:ring-2 ring-brand text-sm";
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`gamerow-${athlete.athlete_id}-${gameNumber}`}>
      <span className="w-6 text-xs font-black text-onsurface3">G{gameNumber}</span>
      <input className={`${input} w-20`} placeholder={isReps ? "kg" : "m:ss.cc"}
        inputMode={isReps ? "numeric" : "text"} value={time} onChange={(e) => setTime(e.target.value)} />
      <input className={`${input} w-12`} placeholder="lane" inputMode="numeric" value={lane}
        onChange={(e) => setLane(e.target.value)} data-testid={`gamelane-${athlete.athlete_id}-${gameNumber}`} />
      <button onClick={() => setOverride(!override)} className={`p-2 rounded-lg ${override ? "bg-brand text-onbrand" : "bg-surface text-onsurface3"}`}
        data-testid={`override-${athlete.athlete_id}-${gameNumber}`}>
        {override ? <Unlock size={16} /> : <Lock size={16} />}
      </button>
      <input className={`${input} w-14 ${override ? "" : "opacity-50"}`} type="number" value={points}
        disabled={!override} onChange={(e) => setPoints(e.target.value)} placeholder="pt" />
      <button onClick={save} disabled={busy} className="ml-auto bg-black text-brand rounded-lg p-2" data-testid={`gamesave-${athlete.athlete_id}-${gameNumber}`}>
        <Save size={18} />
      </button>
    </div>
  );
}

function BatteriaSchedule({ gender, suit, batteria }) {
  const [w, setW] = useState(batteria.warmup_time || "");
  const [r, setR] = useState(batteria.race_time || "");
  const save = async () => {
    try {
      await api.post("/games/batteria-schedule", {
        gender, suit, batteria_index: batteria.batteria_index, warmup_time: w, race_time: r,
      }, { headers: adminHeaders() });
      toast.success("Orari salvati");
    } catch { toast.error("Errore"); }
  };
  const input = "bg-surface rounded-lg px-2 py-1.5 font-bold text-onsurface outline-none focus:ring-2 ring-brand text-sm w-24";
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs text-onsurface3 font-bold">Warm up</span>
      <input className={input} placeholder="HH:MM" value={w} onChange={(e) => setW(e.target.value)} />
      <span className="text-xs text-onsurface3 font-bold">Gara</span>
      <input className={input} placeholder="HH:MM" value={r} onChange={(e) => setR(e.target.value)} />
      <button onClick={save} className="bg-black text-brand rounded-lg p-2" data-testid={`batt-sched-save-${suit}-${batteria.batteria_index}`}><Check size={16} /></button>
    </div>
  );
}

function SuitConfig({ gender, suit, currentNum, onSaved }) {
  const [n, setN] = useState(currentNum || 1);
  const save = async () => {
    try {
      await api.post("/games/config", { gender, suit, num_batterie: Number(n) || 1 }, { headers: adminHeaders() });
      toast.success("Batterie aggiornate");
      onSaved();
    } catch { toast.error("Errore"); }
  };
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-onsurface3 font-bold">Num batterie</span>
      <input type="number" min="1" value={n} onChange={(e) => setN(e.target.value)}
        className="bg-surface rounded-lg px-2 py-1.5 font-bold text-onsurface outline-none focus:ring-2 ring-brand text-sm w-16"
        data-testid={`config-num-${suit}`} />
      <button onClick={save} className="bg-black text-brand rounded-lg p-2" data-testid={`config-save-${suit}`}><Check size={16} /></button>
    </div>
  );
}

export default function AdminGames() {
  const nav = useNavigate();
  const [gender, setGender] = useState("Men");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/games?gender=${gender}`).then((r) => setData(r.data)).catch(() => {});
  }, [gender]);
  useEffect(() => { if (!getToken()) { nav("/admin"); return; } load(); }, [load, nav]);

  return (
    <div className="min-h-screen bg-surface pb-10" data-testid="admin-games-page">
      <Toaster position="top-center" richColors />
      <header className="bg-black px-4 pb-4" style={{ paddingTop: "calc(var(--safe-top) + 16px)" }}>
        <div className="flex items-center gap-3">
          <button onClick={() => nav("/admin")} className="text-white"><ChevronLeft size={32} strokeWidth={2.5} /></button>
          <h1 className="text-2xl font-black text-white">Gestisci Games</h1>
        </div>
        <div className="mt-4 flex gap-2 bg-[#1c1c1e] p-1 rounded-full">
          {[["Men", "Uomo"], ["Women", "Donna"]].map(([v, l]) => (
            <button key={v} data-testid={`games-gender-${v.toLowerCase()}`} onClick={() => setGender(v)}
              className={`flex-1 py-2.5 rounded-full font-bold ${gender === v ? "bg-brand text-onbrand" : "text-white"}`}>{l}</button>
          ))}
        </div>
      </header>

      <div className="px-4 pt-4">
        {!data ? null : data.suits.length === 0 ? (
          <p className="text-onsurface3 text-center py-16">Nessun atleta con round. Aggiungi i round del Ranking Workout per generare i semi.</p>
        ) : (
          data.suits.map((s) => (
            <div key={s.suit} className="mb-6">
              <div className="flex items-center justify-between bg-surface2 rounded-2xl px-4 py-3 mb-2">
                <span className="font-black text-lg flex items-center gap-2">
                  <span style={{ color: SUIT_COLOR[s.suit] }}>{SUIT_SYMBOL[s.suit]}</span>{SUIT_LABEL[s.suit]}
                </span>
                <SuitConfig gender={gender} suit={s.suit} currentNum={s.num_batterie} onSaved={load} />
              </div>

              {s.batterie.map((b) => (
                <div key={b.batteria_index} className="bg-surface2 rounded-2xl p-4 mb-3">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <span className="bg-black text-brand rounded-full px-4 py-1 text-sm font-black">BATTERIA {b.batteria_index + 1}</span>
                  </div>
                  <BatteriaSchedule gender={gender} suit={s.suit} batteria={b} />
                  <div className="mt-4 space-y-4">
                    {b.athletes.map((a) => (
                      <div key={a.athlete_id} className="border-t border-surface3 pt-3">
                        <p className="font-black text-onsurface mb-2">{a.is_finalist ? "👑 " : ""}{a.name} <span className="text-onsurface3 text-sm font-medium">Lane {a.lane || "—"}</span></p>
                        <div className="space-y-2">
                          {[1, 2, 3].map((g) => (
                            <GameRow key={g} gender={gender} athlete={a} gameNumber={g} onSaved={load} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
      <BottomTab />
    </div>
  );
}
